import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";
import DropZone from "../components/DropZone";

function resolveSrc(u) { if (!u) return ""; if (u.startsWith("http")) return u; if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`; return u; }

const DEFAULTS = {
  brand_name: "places", brand_accent_char: ".", brand_accent_color: "#FF5E00",
  logo_url: "", logo_url_dark: "", logo_height: 40, marquee_speed_seconds: 40,
  testimonials_speed_seconds: 60, blob_animation_speed_seconds: 24,
  accent_yellow: "#D4FF00", accent_orange: "#FF5E00", accent_green: "#00E59B", favicon_url: "",
};

export default function BrandingPage() {
  const [data, setData] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content/kv/branding").then(({data:v}) => v && setData({...DEFAULTS, ...v})); }, []);
  const upd = (k, v) => setData(s => ({...s, [k]: v}));
  const save = async () => {
    setSaving(true);
    try { await api.put("/content/kv/branding", { value: data }); toast.success("Marca atualizada. Atualize a página pública para ver as mudanças."); }
    catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const colorField = (key, label) => (
    <div>
      <label className="text-xs text-neutral-500">{label}</label>
      <div className="flex gap-2 items-center">
        <input type="color" value={data[key]} onChange={(e) => upd(key, e.target.value)} className="w-12 h-9 border border-black/15 rounded cursor-pointer" />
        <input value={data[key]} onChange={(e) => upd(key, e.target.value)} className="flex-1 border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <div><h1 className="font-display text-4xl">Marca & Visual</h1><p className="text-sm text-neutral-500 mt-1">Logo, cores de acento e velocidade do marquee.</p></div>
        <button onClick={save} disabled={saving} data-testid="branding-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}</button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <section className="border border-black/10 p-5 rounded space-y-4">
          <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Logo</div>
          <div>
            <label className="text-xs text-neutral-500">Logo (fundo claro)</label>
            {data.logo_url && <img src={resolveSrc(data.logo_url)} alt="" className="h-14 my-2 object-contain" />}
            <DropZone multiple={false} onUploaded={(f) => upd("logo_url", f.url)} />
            <input value={data.logo_url} onChange={(e) => upd("logo_url", e.target.value)} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-2" data-testid="branding-logo-url" />
          </div>
          <div>
            <label className="text-xs text-neutral-500">Logo dark (footer/seções escuras)</label>
            {data.logo_url_dark && <div className="bg-black inline-block p-2 my-2 rounded"><img src={resolveSrc(data.logo_url_dark)} alt="" className="h-14 object-contain" /></div>}
            <DropZone multiple={false} onUploaded={(f) => upd("logo_url_dark", f.url)} />
          </div>
          <div><label className="text-xs text-neutral-500">Altura do logo (px)</label><input type="number" value={data.logo_height} onChange={(e) => upd("logo_height", Number(e.target.value))} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-neutral-500">Nome da marca (texto)</label><input value={data.brand_name} onChange={(e) => upd("brand_name", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
            <div><label className="text-xs text-neutral-500">Acento (caractere)</label><input value={data.brand_accent_char} onChange={(e) => upd("brand_accent_char", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" maxLength={3} /></div>
          </div>
        </section>

        <section className="border border-black/10 p-5 rounded space-y-4">
          <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Cores & Movimento</div>
          {colorField("brand_accent_color", "Cor do acento da marca")}
          {colorField("accent_yellow", "Acento principal (amarelo)")}
          {colorField("accent_orange", "Acento secundário (laranja)")}
          {colorField("accent_green", "Acento de destaque (verde)")}
          <div>
            <label className="text-xs text-neutral-500 flex justify-between"><span>Velocidade do marquee de serviços</span><span className="font-mono">{data.marquee_speed_seconds}s</span></label>
            <input type="range" min={10} max={80} step={1} value={data.marquee_speed_seconds} onChange={(e) => upd("marquee_speed_seconds", Number(e.target.value))} data-testid="branding-marquee-speed" className="w-full" />
            <div className="text-[10px] text-neutral-400 flex justify-between"><span>Mais rápido</span><span>Mais lento</span></div>
          </div>
          <div>
            <label className="text-xs text-neutral-500 flex justify-between"><span>Velocidade do marquee de depoimentos</span><span className="font-mono">{data.testimonials_speed_seconds}s</span></label>
            <input type="range" min={20} max={120} step={2} value={data.testimonials_speed_seconds} onChange={(e) => upd("testimonials_speed_seconds", Number(e.target.value))} data-testid="branding-testimonials-speed" className="w-full" />
            <div className="text-[10px] text-neutral-400 flex justify-between"><span>Mais rápido</span><span>Mais lento</span></div>
          </div>
          <div>
            <label className="text-xs text-neutral-500 flex justify-between"><span>Velocidade dos blobs animados (fundo)</span><span className="font-mono">{data.blob_animation_speed_seconds}s</span></label>
            <input type="range" min={2} max={60} step={0.5} value={data.blob_animation_speed_seconds} onChange={(e) => upd("blob_animation_speed_seconds", Number(e.target.value))} data-testid="branding-blob-speed" className="w-full" />
            <div className="text-[10px] text-neutral-400 flex justify-between"><span>Muito rápido (2s)</span><span>Muito lento (60s)</span></div>
          </div>
          <div>
            <label className="text-xs text-neutral-500">Favicon (URL)</label>
            <input value={data.favicon_url} onChange={(e) => upd("favicon_url", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
          </div>
        </section>
      </div>
    </div>
  );
}
